import { RubyGemMark } from '../../../components/RubyGemMark';
import React, { useEffect, useMemo, useState } from 'react';
import { X, AlertTriangle, Save, Boxes, Plus, Minus, Trash2, Monitor, CreditCard, Package, Cable , Paperclip, Megaphone, Wrench, Search } from 'lucide-react';
import ImageGallery from './ImageGallery';
import ShimmerImage from '../../../components/ShimmerImage';
import StatusBar, { BUNDLE_STATUS_OPTIONS, BUNDLE_DRAFT_OPTION } from './StatusBar';
import {
  hardwareApi, money, CATEGORY_LABEL, type DeviceBundleRow, type DeviceModelRow,
} from '../../../services/hardwareApi';
import { billingApi, offerLabel, type BillingSkuRow } from '../../../services/billingApi';
import { quoteKit, quoteLabel, fallbackNote } from '../../inventory/bundleFinance';
import { RubyLoader } from '../../../components/RubyLoader';

const CATEGORY_ICON: Record<string, React.FC<any>> = {
  pos_terminal: Monitor, payment_device: CreditCard, peripheral: Package, accessory: Cable,
  office_supply: Paperclip, marketing_material: Megaphone, tools: Wrench,
};

const centsToInput = (c: number | undefined) => (typeof c === 'number' ? (c / 100).toFixed(2) : '');
const inputToCents = (v: string) => {
  const n = Number.parseFloat(v);
  return Number.isFinite(n) ? Math.round(n * 100) : 0;
};

interface Props {
  bundleId: string | null;
  models: DeviceModelRow[];
  onClose: () => void;
  onSaved: () => void;
  /**
   * COPY MODE — create a new kit seeded from an existing one.
   *
   * Only meaningful when `bundleId` is null: this is a CREATE that starts from
   * somebody else's parts list rather than from nothing. The source bundle is
   * read and never written — nothing exists until Save, so abandoning a copy
   * leaves no half-built kit behind.
   */
  copyFromId?: string | null;
  /**
   * `modal` (default) is the original dialog, still used by the Bundles list.
   *
   * `page` drops the overlay and the 92vh cap so the builder lays out on a
   * route of its own — building a kit is a real piece of work with room to
   * grow, and a dialog that scrolls inside a scrolling page is a ceiling on
   * how much of it can ever be shown at once. Mirrors `DeviceEditor`'s
   * variant, so the two editors behave the same way.
   */
  variant?: 'modal' | 'page';
}

const blank = () => ({
  bundleId: '', name: '', description: '', heroImageUrl: '',
  photos: [] as string[],
  components: [] as { modelId: string; quantity: number }[],
  combinedOutboundShippingCents: 0,
  status: 'draft', displayOrder: 100,
});

/**
 * A FREE ID FOR THE COPY. `bdl_starter` becomes `bdl_starter_copy`, then
 * `_copy_2`, `_copy_3` — the first one nothing else is already using.
 *
 * It is only a SUGGESTION: the field stays editable in copy mode, because the
 * ID is what every deal and order refers to the kit by and is worth naming
 * properly. What it must never be is a name already taken — see `idTaken`.
 */
const suggestCopyId = (sourceId: string, taken: Set<string>) => {
  const base = `${sourceId || 'bdl'}_copy`;
  if (!taken.has(base)) return base;
  for (let n = 2; n < 200; n += 1) {
    if (!taken.has(`${base}_${n}`)) return `${base}_${n}`;
  }
  return `${base}_${Date.now()}`;
};

/** The authored fields of a kit — everything the server derives is left behind. */
const authoredFrom = (b: DeviceBundleRow) => ({
  name: b.name,
  description: b.description || '',
  heroImageUrl: b.heroImageUrl || '',
  photos: b.photos?.length ? [...b.photos] : (b.heroImageUrl ? [b.heroImageUrl] : []),
  components: (b.components || []).map((c) => ({ ...c })),
  combinedOutboundShippingCents: b.combinedOutboundShippingCents || 0,
  displayOrder: b.displayOrder ?? 100,
});

const BundleBuilder: React.FC<Props> = ({ bundleId, models, onClose, onSaved, copyFromId = null, variant = 'modal' }) => {
  const creating = !bundleId;
  const copying = creating && !!copyFromId;
  const [form, setForm] = useState<any>(blank());
  const [server, setServer] = useState<DeviceBundleRow | null>(null);
  const [loading, setLoading] = useState(!!bundleId || copying);
  /**
   * EVERY ID ALREADY IN USE. `upsertBundle` MERGES into the stored row, so
   * saving a new kit under an existing ID does not fail — it quietly rewrites
   * that kit. Only loaded while creating, since editing cannot collide with
   * anything but itself. `null` = not read yet.
   */
  const [takenIds, setTakenIds] = useState<Set<string> | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** Component picker: free-text search and a category pill. '' = all. */
  const [addQuery, setAddQuery] = useState('');
  const [addCategory, setAddCategory] = useState('');
  /** The suggestion strip is a prompt, not a demand — it can be put away. */
  const [hideSuggested, setHideSuggested] = useState(false);
  /**
   * EVERY DEVICE SKU IN THE CATALOG. `null` while loading, `[]` if the call
   * failed — the finance panel says which, and never blocks the builder: a kit
   * can be assembled whether or not its parts' terms could be read.
   */
  const [deviceSkus, setDeviceSkus] = useState<BillingSkuRow[] | null>(null);
  const [skuError, setSkuError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    billingApi.listSkus()
      .then((r) => { if (!cancelled) setDeviceSkus((r.items || []).filter((s) => s.type === 'device_finance')); })
      .catch(() => { if (!cancelled) { setDeviceSkus([]); setSkuError(true); } });
    return () => { cancelled = true; };
  }, []);

  /**
   * THE IDS ALREADY SPOKEN FOR. Best-effort: if the list cannot be read the
   * builder still works, it just cannot warn about a collision — so the guard
   * below only ever blocks on an id it has actually SEEN taken.
   */
  useEffect(() => {
    if (!creating) return undefined;
    let cancelled = false;
    hardwareApi.listBundles()
      .then((r) => { if (!cancelled) setTakenIds(new Set((r.items || []).map((b) => b.bundleId))); })
      .catch(() => { if (!cancelled) setTakenIds(new Set()); });
    return () => { cancelled = true; };
  }, [creating]);

  useEffect(() => {
    if (!bundleId && !copyFromId) { setForm(blank()); setLoading(false); return undefined; }
    let cancelled = false;
    (async () => {
      try {
        const res = await hardwareApi.getBundle((bundleId || copyFromId)!);
        if (cancelled) return;
        const hero = res.bundle.heroImageUrl || '';
        // Length check, not `||`: an empty array is TRUTHY, so `photos || fallback`
        // silently returns [] and the stored image never renders.
        const stored = res.bundle.photos?.length ? res.bundle.photos : (hero ? [hero] : []);
        if (bundleId) {
          setForm({ ...blank(), ...res.bundle, heroImageUrl: hero, photos: stored });
          setServer(res.bundle);
          return;
        }
        /**
         * A COPY IS A NEW KIT, NOT A SECOND VIEW OF THE OLD ONE.
         *
         * It takes the parts, media, description and shipping — the work worth
         * not doing twice — and NOTHING that identifies the original: its id,
         * its place in the world, or its sellability. It starts as a DRAFT
         * called "… (copy)" so a duplicate can never appear alongside its
         * source as a second live kit that somebody meant to edit first.
         *
         * `server` is deliberately left null: there is no saved row behind this
         * form yet, and pointing it at the SOURCE row would have the cost box
         * report the original's saved totals as this kit's.
         */
        setForm({
          ...blank(),
          ...authoredFrom(res.bundle),
          heroImageUrl: hero,
          photos: stored,
          bundleId: '',
          name: `${res.bundle.name} (copy)`.slice(0, 120),
          status: 'draft',
        });
      } catch (e: any) {
        if (!cancelled) setError(e?.message || `Failed to load ${bundleId ? 'bundle' : 'the bundle to copy'}`);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [bundleId, copyFromId]);

  /**
   * The suggested id, filled in once BOTH the source kit and the taken list
   * have arrived — and only into an empty field, so it never overwrites an id
   * the operator has already started typing.
   */
  useEffect(() => {
    if (!copying || !takenIds || loading || form.bundleId) return;
    setForm((f: any) => (f.bundleId ? f : { ...f, bundleId: suggestCopyId(copyFromId!, takenIds) }));
  }, [copying, takenIds, loading, form.bundleId, copyFromId]);

  const byId = useMemo(() => Object.fromEntries(models.map((m) => [m.modelId, m])), [models]);

  /**
   * Live preview while editing. The AUTHORITATIVE numbers come back from the
   * service on save (`server`); this exists only so the panel is not frozen
   * between keystrokes.
   *
   * `landedCostCents` is taken straight off the row — the service derives it,
   * so there is no second implementation of the maths to drift.
   */
  const preview = useMemo(() => {
    let comp = 0;
    const missing: string[] = [];
    for (const c of form.components) {
      const m = byId[c.modelId];
      if (!m) { missing.push(c.modelId); continue; }
      comp += m.landedCostCents * c.quantity;
    }
    return {
      componentsLandedCents: comp,
      totalCostCents: comp + (form.combinedOutboundShippingCents || 0),
      missing,
    };
  }, [form.components, form.combinedOutboundShippingCents, byId]);

  // The host terminal constrains what else may go in the kit.
  const host = useMemo(
    () => form.components.map((c: any) => byId[c.modelId]).find((m: any) => m?.category === 'pos_terminal'),
    [form.components, byId],
  );

  const chosen = new Set<string>(form.components.map((c: any) => c.modelId));

  const addable = useMemo(() => {
    // Drafts ARE offered, clearly marked. Silently hiding them is why a device
    // someone just created appeared to vanish. Retired devices stay hidden —
    // there is no reason to build a new kit around one.
    const pool = models.filter((m) => !chosen.has(m.modelId)
      && m.lifecycle?.status !== 'eol' && m.lifecycle?.status !== 'discontinued');
    if (!host) return pool;
    const allowed = new Set<string>(host.compatibility?.worksWith || []);
    // Once a terminal is chosen, only its declared companions (plus other
    // terminals, which the server will reject for an ACTIVE bundle) are offered.
    return pool.filter((m) => m.category === 'pos_terminal' || allowed.size === 0 || allowed.has(m.modelId));
  }, [models, chosen, host]);

  /**
   * WHAT THE TERMINAL ITSELF SAYS IT WORKS WITH.
   *
   * Every POS device declares its companions on its own page; that list is the
   * answer to "what else goes in this kit", and it was reachable here only by
   * scrolling a filtered picker and knowing which names to look for. Offered
   * as a strip instead — add, adjust the quantity, or ignore it entirely and
   * carry on with the picker below.
   *
   * Devices ALREADY in the kit stay in the strip, showing their quantity, so
   * the list reads as "the terminal's companions and where each one stands"
   * rather than silently shrinking as they are added.
   */
  const suggested = useMemo(() => {
    if (!host) return [] as DeviceModelRow[];
    return (host.compatibility?.worksWith || [])
      .map((id: string) => byId[id])
      .filter((m: DeviceModelRow | undefined): m is DeviceModelRow => !!m
        && m.lifecycle?.status !== 'eol' && m.lifecycle?.status !== 'discontinued');
  }, [host, byId]);

  /**
   * What the PICKER offers, once the suggestion strip has taken its share.
   * Showing the terminal's companions in both places made the strip look like
   * a shortcut to a list right under it — the same devices, twice.
   */
  const pickerPool = useMemo(() => {
    if (!host || hideSuggested || suggested.length === 0) return addable;
    const shown = new Set(suggested.map((m) => m.modelId));
    return addable.filter((m) => !shown.has(m.modelId));
  }, [addable, host, hideSuggested, suggested]);

  /**
   * THE PICKER'S OWN SEARCH AND CATEGORY FILTER. A catalog of one screen's
   * worth of chips became a wall the moment there were thirty devices, and the
   * part an operator wanted ("the printers") had no way to be asked for.
   */
  const visibleAddable = useMemo(() => {
    const q = addQuery.trim().toLowerCase();
    return pickerPool.filter((m) => {
      if (addCategory && m.category !== addCategory) return false;
      if (!q) return true;
      return [m.modelName, m.brand, CATEGORY_LABEL[m.category], m.manufacturer]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q));
    });
  }, [pickerPool, addQuery, addCategory]);

  /** One pill per category that actually has something to add, with its count. */
  const addCategoryPills = useMemo(() => {
    const counts = new Map<string, number>();
    pickerPool.forEach((m) => counts.set(m.category, (counts.get(m.category) || 0) + 1));
    return Object.keys(CATEGORY_ICON)
      .filter((c) => counts.has(c))
      .map((c) => ({ value: c, label: CATEGORY_LABEL[c] || c, count: counts.get(c) || 0 }));
  }, [pickerPool]);

  const incompatible = useMemo(() => {
    if (!host) return [] as string[];
    const allowed = new Set<string>(host.compatibility?.worksWith || []);
    if (allowed.size === 0) return [];
    return form.components
      .filter((c: any) => c.modelId !== host.modelId && byId[c.modelId] && !allowed.has(c.modelId))
      .map((c: any) => c.modelId);
  }, [form.components, host, byId]);

  /** Draft components in an ACTIVE bundle — the kit would ship something unfinished. */
  const draftComponents = useMemo(
    () => form.components
      .filter((c: any) => byId[c.modelId]?.lifecycle?.status === 'draft')
      .map((c: any) => c.modelId),
    [form.components, byId],
  );

  const unmetRequires = useMemo(() => {
    const out = new Set<string>();
    for (const c of form.components) {
      for (const req of byId[c.modelId]?.compatibility?.requires || []) {
        if (!chosen.has(req)) out.add(req);
      }
    }
    return [...out];
  }, [form.components, byId, chosen]);

  /**
   * FINANCE COMES FROM THE PARTS, NOT FROM THE KIT.
   *
   * Selling terms are attached to a DEVICE — that is where the wizard writes
   * them and where the agreement hangs — so a bundle has no terms of its own
   * to set. What it has is the terms of everything in it, and until now the
   * builder said nothing about them at all: an operator could assemble a kit
   * out of devices half of which could not be sold, and find out later.
   *
   * Each component carries its own offers here, and the totals below add up
   * the CHEAPEST offer per mode, times the quantity in the kit.
   */
  const finance = useMemo(() => {
    const rows = form.components.map((c: any) => {
      const m = byId[c.modelId];
      const offers = (deviceSkus || []).filter((s) => s.modelId === c.modelId);
      return {
        modelId: c.modelId as string,
        name: m?.modelName || c.modelId,
        heroImageUrl: m?.media?.heroImageUrl,
        category: m?.category,
        quantity: c.quantity as number,
        offers,
      };
    });

    // The arithmetic — including what happens to a rental-only part when the
    // kit is quoted outright — is `bundleFinance`'s, shared with the Bundles
    // list so the two cannot state different prices for the same kit.
    const quotes = quoteKit(rows.map((r) => ({
      modelId: r.modelId, name: r.name, quantity: r.quantity, offers: r.offers,
    })));

    return {
      rows,
      quotes,
      withoutTerms: rows.filter((r) => r.offers.length === 0).map((r) => r.name),
    };
  }, [form.components, byId, deviceSkus]);

  const setQty = (modelId: string, delta: number) => {
    setForm((f: any) => ({
      ...f,
      components: f.components
        .map((c: any) => (c.modelId === modelId ? { ...c, quantity: Math.max(0, c.quantity + delta) } : c))
        .filter((c: any) => c.quantity > 0),
    }));
  };

  const add = (modelId: string) => {
    setForm((f: any) => ({ ...f, components: [...f.components, { modelId, quantity: 1 }] }));
  };

  const remove = (modelId: string) => {
    setForm((f: any) => ({ ...f, components: f.components.filter((c: any) => c.modelId !== modelId) }));
  };

  const blockedByIncompat = form.status === 'active' && incompatible.length > 0;
  const blockedByDraft = form.status === 'active' && draftComponents.length > 0;
  /**
   * THE ID IS ALREADY SOMEBODY ELSE'S. A blocker, not a warning: `upsertBundle`
   * merges, so saving over a taken id silently REPLACES that kit's parts,
   * photos and price with these — and the deals already sold against it would
   * quietly start resolving to different hardware.
   */
  const idTaken = creating && !!form.bundleId && !!takenIds?.has(form.bundleId);
  const canSave =
    !!form.bundleId && !!form.name && form.components.length > 0 && !idTaken &&
    preview.missing.length === 0 && !blockedByIncompat && !blockedByDraft && !saving;

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      const res = await hardwareApi.upsertBundle({
        ...form,
        heroImageUrl: form.heroImageUrl || undefined,
      });
      setServer(res.bundle);
      onSaved();
    } catch (e: any) {
      setError(e?.message || 'Failed to save bundle');
    } finally {
      setSaving(false);
    }
  };

  const nameOf = (id: string) => byId[id]?.modelName || id;

  const asPage = variant === 'page';

  return (
    <div className={asPage
      ? ''
      : 'fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4'}>
      {/* PAGE: no overlay, no height cap — the route scrolls, and the builder
          takes the width it is given so a bigger design has somewhere to go.
          MODAL: still a panel, because there it genuinely floats over one. */}
      <div className={asPage
        ? 'flex w-full flex-col overflow-hidden rounded-2xl border border-gray-200 bg-gray-50 text-gray-900'
        : 'flex max-h-[92vh] w-full max-w-[1000px] flex-col overflow-hidden rounded-2xl bg-gray-50 text-gray-900 shadow-2xl'}>
        {/* THE ROUTE ALREADY HAS A HEADER. On a page the black band is the
            second one on screen, saying the same thing directly under the
            first — so only the dialog draws it. Cancel in the footer is the
            way out; the X was the only thing the band carried that the page
            did not already have. */}
        {/* PAGE ACTION BAR — the first thing in the card, and sticky, so Save
            and Cancel are reachable from anywhere in a long form rather than
            only from its bottom. */}
        {asPage && (
          <div className="sticky top-0 z-10 flex flex-wrap items-center justify-between gap-3 border-b border-gray-200 bg-white px-6 py-3">
            <div className="min-w-0 flex-1 text-sm">
              {error ? (
                <span className="flex items-start gap-1.5 text-red-700">
                  <AlertTriangle size={15} className="mt-0.5 shrink-0" />
                  {error}
                </span>
              ) : (
                <span className="text-[12px] text-gray-500">
                  {copying
                    ? 'A copy — nothing is created until you save, and the original is untouched.'
                    : 'A named kit — this is what a customer sees on a deal.'}
                </span>
              )}
            </div>
            <div className="flex shrink-0 gap-2">
              <button onClick={onClose} className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50">
                Cancel
              </button>
              <button
                onClick={save}
                disabled={!canSave}
                className="flex items-center gap-1.5 rounded-lg px-4 py-2 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-40"
                style={{ background: '#DC143C' }}
              >
                {saving ? <RubyGemMark size={15} drawing color="currentColor" /> : <Save size={15} />}
                Save bundle
              </button>
            </div>
          </div>
        )}
        {!asPage && (
          <div className="flex shrink-0 items-center justify-between bg-black px-6 py-4 text-white">
            <div className="flex items-center gap-3">
              <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-gray-800">
                <Boxes size={20} />
              </div>
              <div>
                <h2 className="text-lg font-bold">{bundleId ? form.name || form.bundleId : copying ? 'Copy bundle' : 'New bundle'}</h2>
                <p className="mt-0.5 text-xs text-gray-400">A named kit — this is what a customer sees on a deal.</p>
              </div>
            </div>
            <button onClick={onClose} className="rounded-lg p-2 text-gray-400 hover:bg-gray-800 hover:text-white">
              <X size={18} />
            </button>
          </div>
        )}

        {loading ? (
          <RubyLoader variant="page" />
        ) : (
          <div className="flex-1 overflow-y-auto"><div className={`grid gap-5 p-6 ${asPage
            /* PAGE: the two halves are not equal work. The left column holds
               identity, media and status; the right holds a short cost box,
               which sticks. Components is neither — it is a full-width row of
               its own beneath them both. */
            ? 'lg:grid-cols-[minmax(0,1.7fr)_minmax(0,1fr)]'
            : 'lg:grid-cols-2'}`}>
            {/* LEFT — identity, media, status */}
            <div className="space-y-5">
              <section className="rounded-xl border border-gray-200 bg-white p-5">
                <h3 className="mb-4 text-xs font-bold uppercase tracking-wide text-gray-500">Identity</h3>
                <label className="mb-1 block text-xs font-semibold text-gray-700">Bundle ID</label>
                <input value={form.bundleId} onChange={(e) => setForm({ ...form, bundleId: e.target.value.trim() })}
                  disabled={!!bundleId} placeholder="bdl_starter"
                  className={`w-full rounded-lg border bg-white text-stone-800 px-3 py-2 font-mono text-sm disabled:bg-gray-100 disabled:text-gray-500 ${
                    idTaken ? 'border-red-400' : 'border-gray-300'}`} />
                {idTaken ? (
                  <p className="mb-3 mt-1 text-[11px] font-semibold text-red-700">
                    <strong className="font-mono">{form.bundleId}</strong> is an existing bundle — saving would overwrite
                    it, and every deal sold against it would change hardware. Give this copy an id of its own.
                  </p>
                ) : copying ? (
                  <p className="mb-3 mt-1 text-[11px] text-gray-500">
                    A new id — the original keeps its own. This is what deals and orders will refer to the copy by.
                  </p>
                ) : <div className="mb-3" />}
                <label className="mb-1 block text-xs font-semibold text-gray-700">Bundle name</label>
                <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="Starter Till Kit" className="mb-3 w-full rounded-lg border border-gray-300 bg-white text-stone-800 px-3 py-2 text-sm" />
                <label className="mb-1 block text-xs font-semibold text-gray-700">
                  Description <span className="font-normal text-gray-400">— shown on the deal card &amp; landing page</span>
                </label>
                <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3}
                  className="mb-3 w-full rounded-lg border border-gray-300 bg-white text-stone-800 px-3 py-2 text-sm" />
                <div className="mb-3">
                  <ImageGallery
                    photos={form.photos}
                    heroImageUrl={form.heroImageUrl}
                    subject={form.name}
                    onChange={(next) => setForm({ ...form, photos: next.photos, heroImageUrl: next.heroImageUrl })}
                  />
                </div>
                <StatusBar
                  label="Status"
                  /* Draft only while it IS one, and locked — the bar moves a
                     kit forward, it does not put one back. */
                  options={form.status === 'draft'
                    ? [BUNDLE_DRAFT_OPTION, ...BUNDLE_STATUS_OPTIONS]
                    : BUNDLE_STATUS_OPTIONS}
                  value={form.status}
                  onChange={(v) => setForm({ ...form, status: v })}
                />
              </section>

            </div>

            {/* RIGHT — cost + warnings. On a page it sticks, so the
                running total and any warning stay in view while the operator
                works down the component picker. */}
            <div className={`space-y-5 ${asPage ? 'self-start lg:sticky lg:top-[68px]' : ''}`}>
              <section className="rounded-xl border border-gray-200 bg-white p-5">
                <h3 className="mb-3 text-xs font-bold uppercase tracking-wide text-gray-500">Bundle cost</h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Components landed</span>
                    <span className="font-mono">{money(preview.componentsLandedCents)}</span>
                  </div>
                  <div className="flex items-center justify-between gap-3">
                    <span className="text-gray-600">Outbound shipping (combined)</span>
                    <div className="flex items-center gap-1">
                      <span className="text-sm text-gray-400">$</span>
                      <input type="number" step="0.01" min="0"
                        value={centsToInput(form.combinedOutboundShippingCents)}
                        onChange={(e) => setForm({ ...form, combinedOutboundShippingCents: inputToCents(e.target.value) })}
                        className="w-24 rounded-lg border border-gray-300 bg-white text-stone-800 px-2 py-1 text-right font-mono text-sm" />
                    </div>
                  </div>
                  <div className="flex justify-between border-t border-gray-200 pt-2 text-base font-bold">
                    <span>Total cost to us</span>
                    <span className="font-mono">{money(preview.totalCostCents)}</span>
                  </div>
                </div>
                {server && server.totalCostCents !== preview.totalCostCents && (
                  <p className="mt-2 text-[11px] text-gray-500">
                    Saved value: {money(server.totalCostCents)} — save to update.
                  </p>
                )}
                <p className="mt-2 text-[11px] text-gray-500">
                  Ships as one box, so outbound is charged once — not the sum of each part's shipping.
                </p>
              </section>

              {preview.missing.length > 0 && (
                <div className="flex items-start gap-2 rounded-xl border border-red-200 bg-red-50 p-3">
                  <AlertTriangle size={16} className="mt-0.5 shrink-0 text-red-600" />
                  <div className="text-sm text-red-800">
                    <strong>Unknown devices:</strong> <span className="font-mono text-xs">{preview.missing.join(', ')}</span>
                    <div className="mt-1 text-[11px]">A bundle referencing a device that does not exist cannot be costed, so it cannot be saved.</div>
                  </div>
                </div>
              )}

              {incompatible.length > 0 && (
                <div className={`flex items-start gap-2 rounded-xl border p-3 ${form.status === 'active' ? 'border-red-200 bg-red-50' : 'border-amber-200 bg-amber-50'}`}>
                  <AlertTriangle size={16} className={`mt-0.5 shrink-0 ${form.status === 'active' ? 'text-red-600' : 'text-amber-600'}`} />
                  <div className={`text-sm ${form.status === 'active' ? 'text-red-800' : 'text-amber-900'}`}>
                    <strong>Not supported by {host?.modelName}:</strong> {incompatible.map(nameOf).join(', ')}
                    <div className="mt-1 text-[11px]">
                      {form.status === 'active'
                        ? 'An active bundle must be internally consistent — remove these or set the bundle to draft.'
                        : 'Allowed while this bundle is a draft.'}
                    </div>
                  </div>
                </div>
              )}

              {draftComponents.length > 0 && (
                <div className={`flex items-start gap-2 rounded-xl border p-3 ${form.status === 'active' ? 'border-red-200 bg-red-50' : 'border-gray-200 bg-gray-50'}`}>
                  <AlertTriangle size={16} className={`mt-0.5 shrink-0 ${form.status === 'active' ? 'text-red-600' : 'text-gray-500'}`} />
                  <div className={`text-sm ${form.status === 'active' ? 'text-red-800' : 'text-gray-700'}`}>
                    <strong>Still a draft:</strong> {draftComponents.map(nameOf).join(', ')}
                    <div className="mt-1 text-[11px]">
                      {form.status === 'active'
                        ? 'Set the device to Active in the catalog before selling a kit that contains it.'
                        : 'Fine while this bundle is a draft.'}
                    </div>
                  </div>
                </div>
              )}

              {unmetRequires.length > 0 && (
                <div className="flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 p-3">
                  <AlertTriangle size={16} className="mt-0.5 shrink-0 text-amber-600" />
                  <div className="text-sm text-amber-900">
                    <strong>Missing recommended parts:</strong> {unmetRequires.map(nameOf).join(', ')}
                    <div className="mt-1 text-[11px]">
                      A warning, not a blocker — the customer may already own these.
                    </div>
                  </div>
                </div>
              )}

            </div>

            {/* COMPONENTS — ITS OWN FULL-WIDTH ROW beneath both columns. The
                picker is a grid of devices with a search and a filter row, and
                squeezing it into one column wasted the width the page has
                while making the cards narrower than the names on them. */}
              <section className="rounded-xl border border-gray-200 bg-white p-5 lg:col-span-2">
                <h3 className="mb-1 text-xs font-bold uppercase tracking-wide text-gray-500">Components</h3>
                <p className="mb-3 text-[11px] text-gray-500">
                  {host
                    ? <>What is in the kit. The picker below is filtered to what <strong>{host.modelName}</strong> supports — pick from it, or leave a part out.</>
                    : 'What is in the kit. Add a POS device first and the picker narrows to the parts it supports.'}
                </p>

                <div className="space-y-2">
                  {form.components.length === 0 && (
                    <p className="rounded-lg bg-gray-50 p-3 text-center text-[11px] text-gray-500">No components yet.</p>
                  )}
                  {form.components.map((c: any) => {
                    const m = byId[c.modelId];
                    const RowIcon = CATEGORY_ICON[m?.category] || Package;
                    const bad = incompatible.includes(c.modelId);
                    return (
                      <div key={c.modelId}
                        className={`flex items-center gap-3 rounded-lg border p-2.5 ${bad ? 'border-red-300 bg-red-50' : 'border-gray-200'}`}>
                        {/* THE PART'S OWN PICTURE. A kit is read by what is in
                            it, and a column of identical grey icons made every
                            component look like the same device. */}
                        <ShimmerImage
                          src={m?.media?.heroImageUrl}
                          alt={m?.modelName || c.modelId}
                          width={38}
                          height={38}
                          objectFit="contain"
                          className="shrink-0 rounded-lg"
                          fallbackBgColor="bg-gray-100"
                          fallbackIcon={<RowIcon size={16} className="text-gray-400" />}
                        />
                        <div className="min-w-0 flex-1">
                          <div className="truncate text-sm font-semibold text-gray-900">{m?.modelName || c.modelId}</div>
                          <div className="text-[11px] text-gray-500">
                            {m ? CATEGORY_LABEL[m.category] : <span className="text-red-600">unknown device</span>}
                          </div>
                        </div>
                        <div className="flex items-center gap-1">
                          <button onClick={() => setQty(c.modelId, -1)} className="rounded border border-gray-300 bg-white text-stone-800 p-1 hover:bg-gray-100">
                            <Minus size={12} />
                          </button>
                          <span className="w-7 text-center font-mono text-sm text-gray-900">{c.quantity}</span>
                          <button onClick={() => setQty(c.modelId, 1)} className="rounded border border-gray-300 bg-white text-stone-800 p-1 hover:bg-gray-100">
                            <Plus size={12} />
                          </button>
                        </div>
                        <span className="w-20 text-right font-mono text-sm text-gray-700">
                          {m ? money(m.landedCostCents * c.quantity) : '—'}
                        </span>
                        <button onClick={() => remove(c.modelId)} className="rounded p-1 text-gray-400 hover:bg-red-50 hover:text-red-600">
                          <Trash2 size={14} />
                        </button>
                      </div>
                    );
                  })}
                </div>

                {/* SUGGESTED — what the chosen terminal declares it works
                    with, offered right under the kit and above the search.
                    Ignoring it is a valid answer: nothing here is added until
                    it is asked for, and the strip can be put away. */}
                {host && suggested.length > 0 && (
                  <div className="mt-3 rounded-xl border border-emerald-200 bg-emerald-50/50 p-3">
                    <div className="mb-2 flex flex-wrap items-baseline justify-between gap-2">
                      <div className="text-[10px] font-bold uppercase tracking-wide text-emerald-800">
                        Works with {host.modelName} · {suggested.length}
                      </div>
                      <button
                        type="button"
                        onClick={() => setHideSuggested((v) => !v)}
                        className="text-[10.5px] font-semibold text-emerald-700 hover:text-emerald-900"
                      >
                        {hideSuggested ? 'Show' : 'Hide'}
                      </button>
                    </div>
                    {!hideSuggested && (
                      <>
                        <div className="grid grid-cols-1 gap-1.5 sm:grid-cols-2 xl:grid-cols-3">
                          {suggested.map((m) => {
                            const RowIcon = CATEGORY_ICON[m.category] || Package;
                            const inKit = form.components.find((c: any) => c.modelId === m.modelId);
                            return (
                              <div
                                key={m.modelId}
                                className={`flex items-center gap-2 rounded-lg border p-1.5 ${
                                  inKit ? 'border-emerald-300 bg-white' : 'border-emerald-200 bg-white/70'
                                }`}
                              >
                                <ShimmerImage
                                  src={m.media?.heroImageUrl}
                                  alt={m.modelName}
                                  width={32}
                                  height={32}
                                  objectFit="contain"
                                  className="shrink-0 rounded-md"
                                  fallbackBgColor="bg-gray-100"
                                  fallbackIcon={<RowIcon size={14} className="text-gray-400" />}
                                />
                                <div className="min-w-0 flex-1">
                                  <div className="truncate text-[12.5px] font-semibold text-gray-800">{m.modelName}</div>
                                  <div className="truncate text-[10px] text-gray-500">
                                    {[CATEGORY_LABEL[m.category], money(m.landedCostCents)].filter(Boolean).join(' · ')}
                                  </div>
                                </div>
                                {/* IN THE KIT: the quantity, adjustable here.
                                    NOT IN IT: one press to add. */}
                                {inKit ? (
                                  <div className="flex shrink-0 items-center gap-1">
                                    <button onClick={() => setQty(m.modelId, -1)} className="rounded border border-gray-300 bg-white p-1 hover:bg-gray-100">
                                      <Minus size={11} />
                                    </button>
                                    <span className="w-5 text-center font-mono text-[12px] text-gray-900">{inKit.quantity}</span>
                                    <button onClick={() => setQty(m.modelId, 1)} className="rounded border border-gray-300 bg-white p-1 hover:bg-gray-100">
                                      <Plus size={11} />
                                    </button>
                                  </div>
                                ) : (
                                  <button
                                    onClick={() => add(m.modelId)}
                                    className="shrink-0 rounded-md border border-emerald-300 bg-white px-2 py-1 text-[11px] font-semibold text-emerald-700 hover:bg-emerald-50"
                                  >
                                    Add
                                  </button>
                                )}
                              </div>
                            );
                          })}
                        </div>
                        <p className="mt-2 text-[10px] text-emerald-800/70">
                          Declared on {host.modelName}. Add what the kit needs, or ignore this and keep building below.
                        </p>
                      </>
                    )}
                  </div>
                )}

                {pickerPool.length > 0 && (
                  <div className="mt-3 border-t border-gray-100 pt-3">
                    <div className="mb-2 flex flex-wrap items-baseline justify-between gap-2">
                      <div className="text-[10px] font-bold uppercase tracking-wide text-gray-400">
                        {host
                          ? (suggested.length && !hideSuggested ? 'Everything else' : `What ${host.modelName} supports`)
                          : 'Add a device'}
                      </div>
                      <div className="text-[10.5px] text-gray-400">
                        {visibleAddable.length} of {pickerPool.length}
                      </div>
                    </div>

                    {/* SEARCH, then CATEGORY PILLS — the two ways an operator
                        already thinks about the catalog. The pill row scrolls
                        sideways so a new category never pushes the devices
                        down the page. */}
                    <div className="relative mb-2">
                      <Search size={14} className="absolute left-2.5 top-2.5 text-gray-400" />
                      <input
                        value={addQuery}
                        onChange={(e) => setAddQuery(e.target.value)}
                        placeholder="Search by name, brand or category…"
                        className="w-full rounded-lg border border-gray-300 bg-white py-2 pl-8 pr-2 text-[12.5px] text-stone-800"
                      />
                    </div>
                    <div className="mb-2 -mx-0.5 flex gap-1.5 overflow-x-auto px-0.5 pb-1">
                      {[{ value: '', label: 'All', count: pickerPool.length }, ...addCategoryPills].map((p) => {
                        const on = addCategory === p.value;
                        return (
                          <button
                            key={p.value || 'all'}
                            type="button"
                            onClick={() => setAddCategory(p.value)}
                            className={`shrink-0 whitespace-nowrap rounded-full border px-3 py-1 text-[11.5px] font-semibold transition ${
                              on
                                ? 'border-emerald-600 bg-emerald-600 text-white'
                                : 'border-gray-300 bg-white text-gray-600 hover:bg-gray-50 hover:text-gray-800'
                            }`}
                          >
                            {p.label}
                            <span className={`ml-1.5 ${on ? 'text-emerald-100' : 'text-gray-400'}`}>{p.count}</span>
                          </button>
                        );
                      })}
                    </div>

                    {/* PICTURE CARDS, not text chips: the picker is how a kit
                        is assembled, and a name alone does not tell a rack of
                        near-identical model numbers apart. */}
                    {visibleAddable.length === 0 ? (
                      <p className="rounded-lg bg-gray-50 p-3 text-center text-[11.5px] text-gray-400">
                        Nothing matches that search or filter.
                      </p>
                    ) : (
                      <div className="grid max-h-[420px] grid-cols-1 gap-1.5 overflow-y-auto sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
                        {visibleAddable.map((m) => {
                          const RowIcon = CATEGORY_ICON[m.category] || Package;
                          return (
                            <button
                              key={m.modelId}
                              onClick={() => add(m.modelId)}
                              title={`Add ${m.modelName} to this kit`}
                              className="flex items-center gap-2 rounded-lg border border-dashed border-gray-300 p-1.5 text-left transition hover:border-emerald-400 hover:bg-emerald-50"
                            >
                              <ShimmerImage
                                src={m.media?.heroImageUrl}
                                alt={m.modelName}
                                width={34}
                                height={34}
                                objectFit="contain"
                                className="shrink-0 rounded-md"
                                fallbackBgColor="bg-gray-100"
                                fallbackIcon={<RowIcon size={14} className="text-gray-400" />}
                              />
                              <span className="min-w-0 flex-1">
                                <span className="flex items-center gap-1.5">
                                  <span className="truncate text-[12.5px] font-semibold text-gray-800">{m.modelName}</span>
                                  {m.lifecycle?.status === 'draft' && (
                                    <span className="shrink-0 rounded bg-gray-200 px-1 py-0.5 text-[9px] font-bold text-gray-600">DRAFT</span>
                                  )}
                                </span>
                                <span className="block truncate text-[10px] text-gray-400">
                                  {[CATEGORY_LABEL[m.category], m.brand, money(m.landedCostCents)].filter(Boolean).join(' · ')}
                                </span>
                              </span>
                              <Plus size={13} className="shrink-0 text-gray-400" />
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}
              </section>

            {/* FINANCE — READ ONLY, AND FROM THE PARTS. A bundle carries no
                terms of its own; these are the offers already sitting on each
                device, so the operator can see whether the kit he is building
                can actually be sold before he saves it. */}
            {form.components.length > 0 && (
              <section className="rounded-xl border border-gray-200 bg-white p-5 lg:col-span-2">
                <div className="mb-1 flex flex-wrap items-baseline justify-between gap-2">
                  <h3 className="text-xs font-bold uppercase tracking-wide text-gray-500">Finance options</h3>
                  <span className="text-[10.5px] text-gray-400">
                    From the devices — set on each device, never on the kit
                  </span>
                </div>
                <p className="mb-3 text-[11px] text-gray-500">
                  Selling terms belong to a device, so a kit inherits whatever its parts carry. Change them on the
                  device itself — Devices → the device → Selling terms.
                </p>

                {deviceSkus === null ? (
                  <div className="rounded-lg bg-gray-50 p-3"><RubyLoader variant="inline" size={14} label="Reading the price catalog…" /></div>
                ) : (
                  <>
                    {skuError && (
                      <p className="mb-2 rounded-lg border border-amber-200 bg-amber-50 p-2 text-[11px] text-amber-900">
                        The price catalog could not be read, so terms are not shown. The kit itself is unaffected.
                      </p>
                    )}

                    <div className="space-y-1.5">
                      {finance.rows.map((r) => {
                        const RowIcon = CATEGORY_ICON[r.category || ''] || Package;
                        return (
                          <div key={r.modelId} className="flex flex-wrap items-center gap-2 rounded-lg border border-gray-200 p-2">
                            <ShimmerImage
                              src={r.heroImageUrl}
                              alt={r.name}
                              width={30}
                              height={30}
                              objectFit="contain"
                              className="shrink-0 rounded-md"
                              fallbackBgColor="bg-gray-100"
                              fallbackIcon={<RowIcon size={13} className="text-gray-400" />}
                            />
                            <span className="min-w-[160px] flex-1 truncate text-[12.5px] font-semibold text-gray-800">
                              {r.name}
                              {r.quantity > 1 && <span className="ml-1 font-mono text-[11px] text-gray-400">×{r.quantity}</span>}
                            </span>
                            <span className="flex flex-1 flex-wrap items-center gap-1.5">
                              {r.offers.length === 0 ? (
                                <span className="rounded-full border border-amber-300 bg-amber-50 px-2 py-0.5 text-[10.5px] font-semibold text-amber-800">
                                  No selling terms
                                </span>
                              ) : r.offers.map((s) => (
                                <span
                                  key={s.skuId}
                                  title={s.name}
                                  className="rounded-full border border-gray-200 bg-gray-50 px-2 py-0.5 text-[10.5px] text-gray-700"
                                >
                                  <b className="font-semibold capitalize">{s.saleMode || 'offer'}</b>
                                  {` · ${offerLabel(s)}`}
                                </span>
                              ))}
                            </span>
                          </div>
                        );
                      })}
                    </div>

                    {/* THE KIT'S OWN NUMBERS, per mode. Upfront and monthly are
                        stated separately and never summed: a kit holding a
                        rental-only part is "$1,050 upfront + $14.99/mo", and
                        the part that made it so is named. */}
                    {finance.quotes.length > 0 && (
                      <div className="mt-3 grid gap-2 sm:grid-cols-2 xl:grid-cols-4">
                        {finance.quotes.map((q) => (
                          <div key={q.mode} className="rounded-lg border border-gray-200 bg-gray-50 p-2.5">
                            <div className="text-[10px] font-bold uppercase tracking-wide text-gray-500 capitalize">{q.mode}</div>
                            <div className="font-mono text-sm font-bold text-gray-900">{quoteLabel(q, money)}</div>
                            <div className={`mt-0.5 text-[10px] ${q.fallbacks.length || q.missing.length ? 'text-amber-700' : 'text-gray-400'}`}>
                              {fallbackNote(q)
                                || (q.missing.length
                                  ? `${q.covered} of ${finance.rows.length} parts — no terms on ${q.missing.join(', ')}`
                                  : `all ${finance.rows.length} parts`)}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {finance.withoutTerms.length > 0 && (
                      <p className="mt-2 text-[11px] text-amber-800">
                        <strong>{finance.withoutTerms.join(', ')}</strong> {finance.withoutTerms.length === 1 ? 'has' : 'have'}
                        {' '}no selling terms at all — nothing in a deal can price {finance.withoutTerms.length === 1 ? 'it' : 'them'} until that is set on the device.
                      </p>
                    )}
                    {finance.quotes.length === 0 && finance.withoutTerms.length === finance.rows.length && (
                      <p className="mt-2 text-[11px] text-gray-500">
                        None of these devices carries selling terms yet, so this kit has no price to inherit.
                      </p>
                    )}
                  </>
                )}
              </section>
            )}
          </div></div>
        )}

        {/* ACTIONS AT THE BOTTOM — modal only. On a page they sit at the TOP
            (see `actionBar` above): a form this long put Save at the far end
            of a scroll, so which button you could reach depended on where you
            had scrolled to. */}
        {!asPage && (
        <div className="flex shrink-0 items-center justify-between gap-3 border-t border-gray-200 bg-white px-6 py-4">
          <div className="min-h-[20px] flex-1 text-sm">
            {error && (
              <span className="flex items-start gap-1.5 text-red-700">
                <AlertTriangle size={15} className="mt-0.5 shrink-0" />
                {error}
              </span>
            )}
          </div>
          <div className="flex gap-2">
            <button onClick={onClose} className="rounded-lg border border-gray-300 bg-white text-stone-800 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50">
              Cancel
            </button>
            <button
              onClick={save}
              disabled={!canSave}
              className="flex items-center gap-1.5 rounded-lg px-4 py-2 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-40"
              style={{ background: '#DC143C' }}
            >
              {saving ? <RubyGemMark size={15} drawing color="currentColor" /> : <Save size={15} />}
              Save bundle
            </button>
          </div>
        </div>
        )}
      </div>
    </div>
  );
};

export default BundleBuilder;
